import React, { useCallback, useEffect, useRef, useState } from "react";

interface Props {
  ariaLabel: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * ScrollableTabNav - Warm Precision design-system primitive for a horizontally
 * scrolling row of tabs.
 *
 * Overflow is handled with edge arrow buttons instead of a native scrollbar -
 * per the scrollable-tabs pattern (Medium, "Uncover the Full Potential of
 * Perfect Tabs", 2026-08-11 design review): "left and right arrow buttons
 * appear, allowing users to navigate through off-screen tabs" rather than a
 * bare scrollbar. The arrows only render when the row actually overflows its
 * available width right now - never on a screen wide enough to fit every tab -
 * and update live via ResizeObserver as that changes (window resize, sidebar
 * collapse/expand, tabs added or removed).
 *
 * This wraps arbitrary tab children (each page supplies its own tab buttons)
 * rather than owning a tab list itself, so it doesn't assume or enforce any
 * particular tab-button shape - it only owns the scroll container and arrows.
 */
export default function ScrollableTabNav({
  ariaLabel,
  className = "",
  children
}: Props) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    const content = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    // Also watch the content wrapper - the scroller's own size tracks the
    // viewport, so a tab being added/renamed (content growth with no resize
    // of the container) would otherwise never trigger a re-measure.
    if (content) ro.observe(content);
    el.addEventListener("scroll", updateArrows, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", updateArrows);
    };
  }, [updateArrows]);

  // Re-measure whenever the tab children themselves change (covers cases the
  // ResizeObserver's element watch above might miss, e.g. children swapped
  // without a width change on the observed node).
  useEffect(updateArrows, [updateArrows, children]);

  const scrollByPage = (direction: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    el.scrollBy({
      left: direction * Math.round(el.clientWidth * 0.7),
      behavior: reduceMotion ? "auto" : "smooth"
    });
  };

  const overflowing = canScrollLeft || canScrollRight;
  const arrowClass =
    "z-10 shrink-0 flex items-center justify-center w-6 bg-white dark:bg-slate-gray-900 text-zinc-400 hover:text-zinc-700 aria-disabled:opacity-30 aria-disabled:hover:text-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-(--ds-primary)";

  // Both arrows always mount when the row overflows at all - only their
  // aria-disabled/dimmed state toggles per edge. They stay natively enabled
  // (aria-disabled + a click guard, NOT the disabled attribute): setting the
  // real `disabled` attribute on a focused button forces the browser to blur
  // it and move focus to <body> - the exact focus-loss bug this was meant to
  // fix, just moved from "unmount" to "disable". Unmounting a focused button
  // the instant its edge is reached (the original conditional-render
  // approach) had the same symptom and also caused a layout jump every time
  // an arrow appeared/disappeared.
  return (
    <nav
      aria-label={ariaLabel}
      className={`relative flex items-stretch min-w-0 ${className}`}
    >
      {overflowing && (
        <button
          type="button"
          aria-label="Scroll tabs left"
          aria-disabled={!canScrollLeft}
          onClick={() => canScrollLeft && scrollByPage(-1)}
          className={arrowClass}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
      )}
      <div
        ref={scrollerRef}
        className="flex overflow-x-auto flex-1 min-w-0 [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        <div ref={contentRef} className="flex">
          {children}
        </div>
      </div>
      {overflowing && (
        <button
          type="button"
          aria-label="Scroll tabs right"
          aria-disabled={!canScrollRight}
          onClick={() => canScrollRight && scrollByPage(1)}
          className={arrowClass}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      )}
    </nav>
  );
}
